/**
 * Agregar productos a la tienda, o a una seccion propia del home.
 *
 * AGREGAR ES PUBLICAR
 * -------------------
 * El buscador mira el catalogo entero, no solo lo publicado. Lo que ya esta en
 * la tienda aparece como tarjeta (el iPhone 17 entero); lo que no, producto por
 * producto, con "Agregar". Agregar lo publica, lo pone en el home y prende su
 * rubro si estaba oculto (`POST /productos/a-la-tienda`): antes habia que
 * publicarlo en otra pantalla y volver a buscarlo aca.
 *
 * DOS MODOS
 * ---------
 *  · `seccion`: tocar una tarjeta de la tienda la suma o la saca de esa
 *    seccion propia; agregar algo que no estaba lo publica y lo suma.
 *  · `tienda`: solo lo que falta. Agregar lo publica y lo pone en el home, en
 *    su seccion.
 *
 * Un producto sin precio no se ofrece: la tienda no lo mostraria, y agregarlo
 * seria prometer algo que no aparece.
 */
import { useMemo, useState } from "react";
import { formatear } from "@tora/shared";
import { Check, Plus } from "lucide-react";

import { nombreEnTienda, tarjetaCoincide, type TarjetaDelHome } from "@/components/tienda/home";
import { nombreLegible } from "@/components/tienda/logica";
import { Foto } from "@/components/tienda/piezas";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { InputBusqueda } from "@/components/ui/input-busqueda";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import { srcImagenAdmin } from "@/lib/imagenes";
import { aNumero, type Producto } from "@/lib/tipos";
import { cn } from "@/lib/utils";

import { detalleDeProductoUsado, detalleDeUsado } from "./logica";

export function DialogDestacados({
  abierto,
  modo,
  tituloSeccion,
  candidatos,
  elegidas,
  nombres,
  noPublicados,
  agregando,
  onCambiar,
  onAgregar,
  onCerrar,
}: {
  abierto: boolean;
  /** `seccion`: agregar a una seccion propia; `tienda`: solo publicar. */
  modo: "seccion" | "tienda";
  /** El nombre de la seccion, en modo `seccion`. */
  tituloSeccion?: string;
  /** Las tarjetas que ya estan en la tienda (solo se usan en `destacados`). */
  candidatos: readonly TarjetaDelHome[];
  /** Las claves de las tarjetas que ya son destacadas. */
  elegidas: ReadonlySet<string>;
  nombres: Record<string, string> | null | undefined;
  /** Los productos activos que todavia no estan en la tienda. */
  noPublicados: readonly Producto[];
  /** El id que se esta agregando, para no tocarlo dos veces. */
  agregando: number | null;
  onCambiar: (t: TarjetaDelHome, destacar: boolean) => void;
  onAgregar: (p: Producto) => void;
  onCerrar: () => void;
}): JSX.Element {
  const [texto, setTexto] = useState("");
  const q = texto.trim().toLowerCase();

  const tarjetas = useMemo(() => {
    if (modo !== "seccion") return [];
    if (q === "") return candidatos;
    return candidatos.filter((t) => tarjetaCoincide(t, q, nombres));
  }, [modo, candidatos, q, nombres]);

  const nuevos = useMemo(() => {
    if (q === "") return noPublicados;
    return noPublicados.filter((p) =>
      `${p.nombre} ${p.codigo_interno} ${p.marca ?? ""}`.toLowerCase().includes(q),
    );
  }, [noPublicados, q]);

  const nada = tarjetas.length === 0 && nuevos.length === 0;

  return (
    <Dialog open={abierto} onOpenChange={(v) => !v && onCerrar()}>
      <DialogContent tamano="lg" mobile="hoja">
        <DialogHeader>
          <DialogTitle>
            {modo === "seccion"
              ? `Agregar a ${tituloSeccion ?? "la sección"}`
              : "Agregar productos a la tienda"}
          </DialogTitle>
          <DialogDescription>
            {modo === "seccion"
              ? "Tocá los que quieras, de cualquier rubro. Si todavía no estaba en la tienda, se publica."
              : "Lo que agregues se publica y aparece en el home, en su sección."}
          </DialogDescription>
        </DialogHeader>

        <InputBusqueda
          valor={texto}
          onValorChange={setTexto}
          etiqueta="Buscar un producto"
          placeholder="Buscá por nombre o código: ps5, 17 pro, funda…"
        />

        <DialogBody>
          {nada ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              {q === ""
                ? "Todo lo del catálogo ya está en la tienda."
                : `Nada del catálogo coincide con «${texto.trim()}».`}
            </p>
          ) : null}

          {tarjetas.length > 0 ? (
            <section className="space-y-1">
              <TituloSeccion nivel="etiqueta">En la tienda</TituloSeccion>
              <ul className="divide-y divide-border">
                {tarjetas.map((t) => {
                  const elegida = elegidas.has(t.clave);
                  return (
                    <li key={t.clave}>
                      <Fila
                        foto={<Foto producto={t.principal} chico className="size-12 shrink-0" />}
                        nombre={nombreEnTienda(t, nombres)}
                        detalle={
                          // Un usado se dice usado y cual es: el nombre en
                          // limpio le saca el «(USADO)». El codigo, solo si la
                          // tarjeta es UNA fila.
                          t.principal.es_usado
                            ? `Usado · ${detalleDeUsado({
                                color: t.principal.color,
                                bateria_pct: t.principal.bateria_pct,
                                enLocal: t.principal.en_local,
                                esUnidad: t.principal.es_unidad,
                                codigo: t.ids.length === 1 ? t.principal.codigo_interno : null,
                              })} · ${formatear(t.desde, "ARS")}`
                            : formatear(t.desde, "ARS")
                        }
                        pulsado={elegida}
                        onClick={() => onCambiar(t, !elegida)}
                      />
                    </li>
                  );
                })}
              </ul>
            </section>
          ) : null}

          {nuevos.length > 0 ? (
            <section className="space-y-1">
              <TituloSeccion nivel="etiqueta">Todavía no están en la tienda</TituloSeccion>
              <ul className="divide-y divide-border">
                {nuevos.map((p) => {
                  const precio = aNumero(p.precio_venta);
                  const src = srcImagenAdmin(p.imagen_url, p.id);
                  return (
                    <li key={p.id}>
                      <Fila
                        foto={
                          src ? (
                            <img
                              src={src}
                              alt=""
                              loading="lazy"
                              className="size-12 shrink-0 rounded-md object-cover"
                            />
                          ) : (
                            <span className="size-12 shrink-0 rounded-md bg-muted" />
                          )
                        }
                        nombre={nombreLegible(p.nombre)}
                        detalle={
                          precio > 0
                            ? p.es_usado
                              ? `Usado · ${detalleDeProductoUsado(p)} · ${formatear(precio, p.precio_moneda)} · ${p.codigo_interno}`
                              : `${formatear(precio, p.precio_moneda)} · ${p.codigo_interno}`
                            : "Sin precio: cargalo en el catálogo para poder agregarlo"
                        }
                        pulsado={false}
                        deshabilitado={precio <= 0 || agregando === p.id}
                        textoAccion={modo === "seccion" ? "Publicar y agregar" : "Agregar"}
                        onClick={() => onAgregar(p)}
                      />
                    </li>
                  );
                })}
              </ul>
            </section>
          ) : null}
        </DialogBody>

        <DialogFooter>
          <Button type="button" onClick={onCerrar}>
            Listo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Fila({
  foto,
  nombre,
  detalle,
  pulsado,
  deshabilitado,
  textoAccion,
  onClick,
}: {
  foto: React.ReactNode;
  nombre: string;
  detalle: string;
  pulsado: boolean;
  deshabilitado?: boolean;
  /** Con texto, la accion es un boton que dice que hace ("Agregar"); sin, un tilde. */
  textoAccion?: string;
  onClick: () => void;
}): JSX.Element {
  return (
    <Button
      type="button"
      variant="ghost"
      aria-pressed={textoAccion ? undefined : pulsado}
      disabled={deshabilitado}
      onClick={onClick}
      className="h-auto w-full justify-start gap-3 px-2 py-2 text-left"
    >
      {foto}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium">{nombre}</span>
        <span className="block truncate text-xs tabular-nums text-muted-foreground">{detalle}</span>
      </span>
      {textoAccion ? (
        <span className="flex shrink-0 items-center gap-1 rounded-md border border-input px-2 py-1 text-xs font-medium">
          <Plus className="size-3.5" aria-hidden="true" />
          {textoAccion}
        </span>
      ) : (
        <span
          className={cn(
            "flex size-7 shrink-0 items-center justify-center rounded-full border",
            pulsado
              ? "border-primary bg-primary text-primary-foreground"
              : "border-input text-muted-foreground",
          )}
        >
          {pulsado ? <Check aria-hidden="true" /> : <Plus aria-hidden="true" />}
        </span>
      )}
    </Button>
  );
}
